import React from 'react';
import { Box, Flex, Heading, SimpleGrid, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import {
  ArrowSquareUpRight,
  ArrowsLeftRight,
  Buildings,
  FileText,
  Gauge,
  GraduationCap,
  ListChecks,
  Receipt,
  Upload,
} from 'phosphor-react';
import { useNavigate } from 'react-router-dom';

import { GlassCard } from '../../../../../components/glass';
import { useAuth } from '../../../../../providers/auth';
import { InfoTip } from './InfoTip';

type GrupoKey = 'fluxo' | 'cadastros' | 'ia';

interface AtalhoDef {
  label: string;
  description: string;
  path: string;
  icon: React.ReactNode;
  accent: string;
  tint: string;
  grupo: GrupoKey;
  info: string;
  disabled?: boolean;
  // Em vez de navegar, abre um modal (ex: Importar extrato).
  modal?: boolean;
  // Só Admin (1) / Desenvolvimento (5) veem (ex: School AI).
  tecnico?: boolean;
}

const GRUPOS: { key: GrupoKey; titulo: string; dot: string }[] = [
  { key: 'fluxo', titulo: 'Fluxo de trabalho', dot: '#1B88F5' },
  { key: 'cadastros', titulo: 'Cadastros & dados', dot: '#0EA5E9' },
  { key: 'ia', titulo: 'Automação & IA', dot: '#8B5CF6' },
];

// Atalho de destaque (o cockpit — onde fica toda a informação).
const DASHBOARD_ATALHO: AtalhoDef = {
  label: 'Dashboard',
  description: 'Cockpit completo: funil, homologação, fechamentos, volume e mais',
  info: 'Cockpit com todas as métricas do módulo: funil, homologação PDF, empresas, calendário de fechamentos, volume e tabela por empresa.',
  path: '/modulesHome/accountingConciliation/conciliacao-contabil/dashboard',
  icon: <Gauge size={22} weight="duotone" />,
  accent: '#7C3AED',
  tint: '#EDE9FE',
  grupo: 'fluxo',
};

const ATALHOS: AtalhoDef[] = [
  // Fluxo de trabalho
  {
    label: 'Importar extrato',
    description: 'PDF, OFX, XLSX ou ZIP',
    info: 'Envie extratos bancários. O ETL lê e extrai os lançamentos automaticamente (PDF passa por extração/homologação).',
    path: '/modulesHome/accountingConciliation/conciliacao-contabil/importar',
    icon: <Upload size={22} weight="duotone" />,
    accent: '#1B88F5',
    tint: '#DFF3FF',
    grupo: 'fluxo',
    modal: true,
  },
  {
    label: 'Conciliar',
    description: 'Vincule ao plano de contas',
    info: 'Vincule cada lançamento ao plano de contas (débito/crédito) — manual, por regra ou pela IA.',
    path: '/modulesHome/accountingConciliation/conciliacao-contabil/conciliar',
    icon: <ListChecks size={22} weight="duotone" />,
    accent: '#10B981',
    tint: '#D1FAE5',
    grupo: 'fluxo',
  },
  {
    label: 'Exportar Domínio',
    description: 'TXT pronto para importar',
    info: 'Gera o TXT de lançamentos conciliados pronto pro Domínio. Ao exportar, a competência é marcada como fechada.',
    path: '/modulesHome/accountingConciliation/conciliacao-contabil/exportar',
    icon: <ArrowSquareUpRight size={22} weight="duotone" />,
    accent: '#0F766E',
    tint: '#CCFBF1',
    grupo: 'fluxo',
  },
  // Cadastros & dados
  {
    label: 'Empresas',
    description: 'Hub por empresa: saúde, contas, contexto IA',
    info: 'Hub por empresa: saúde da conciliação, contas bancárias e POC, e o contexto que a IA usa.',
    path: '/modulesHome/accountingConciliation/conciliacao-contabil/empresas',
    icon: <Buildings size={22} weight="duotone" />,
    accent: '#0EA5E9',
    tint: '#E0F2FE',
    grupo: 'cadastros',
  },
  {
    label: 'Extratos',
    description: 'Histórico de importações',
    info: 'Histórico de todos os extratos importados + o veredito de cada um (extraído, homologado, com divergência).',
    path: '/modulesHome/accountingConciliation/conciliacao-contabil/extratos',
    icon: <Receipt size={22} weight="duotone" />,
    accent: '#6366F1',
    tint: '#E0E7FF',
    grupo: 'cadastros',
  },
  {
    label: 'Plano de contas',
    description: 'Entenda o plano e a atribuição',
    info: 'Veja o plano de contas da empresa e como cada lançamento é atribuído aos débitos/créditos.',
    path: '/modulesHome/accountingConciliation/conciliacao-contabil/plano-contas',
    icon: <FileText size={22} weight="duotone" />,
    accent: '#6366F1',
    tint: '#E0E7FF',
    grupo: 'cadastros',
  },
  // Automação & IA
  {
    label: 'Regras',
    description: 'Regras inteligentes + personalizadas',
    info: 'Regras de categorização (palavra-chave/contraparte) que automatizam a conciliação. Globais + por empresa.',
    path: '/modulesHome/accountingConciliation/conciliacao-contabil/regras',
    icon: <ArrowsLeftRight size={22} weight="duotone" />,
    accent: '#EC4899',
    tint: '#FCE7F3',
    grupo: 'ia',
  },
  {
    label: 'School AI',
    description: 'Resíduo, memória e progresso da IA',
    info: 'Resíduo da IA, memória aprendida e progresso do modelo (precision/recall, custo). Só técnico.',
    path: '/modulesHome/accountingConciliation/conciliacao-contabil/school-ai',
    icon: <GraduationCap size={22} weight="duotone" />,
    accent: '#8B5CF6',
    tint: '#EDE9FE',
    grupo: 'ia',
    tecnico: true,
  },
];

interface AtalhoCardProps {
  atalho: AtalhoDef;
  index: number;
  onImportarClick?: () => void;
}

const AtalhoCard = ({ atalho, index, onImportarClick }: AtalhoCardProps) => {
  const navigate = useNavigate();
  const handleClick = () => {
    if (atalho.disabled) return;
    if (atalho.modal) {
      onImportarClick?.();
      return;
    }
    navigate(atalho.path);
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.42, delay: 0.04 * index, ease: [0.4, 0, 0.2, 1] }}
    >
      <GlassCard
        intensity="medium"
        hoverable={!atalho.disabled}
        p={4}
        cursor={atalho.disabled ? 'not-allowed' : 'pointer'}
        opacity={atalho.disabled ? 0.62 : 1}
        onClick={handleClick}
        position="relative"
      >
        <Flex direction="column" gap={3}>
          <Flex align="center" justify="space-between">
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              w="44px"
              h="44px"
              borderRadius="xl"
              bg={atalho.tint}
              color={atalho.accent}
              flexShrink={0}
            >
              {atalho.icon}
            </Box>
            <Flex align="center" gap={2}>
              {atalho.disabled && (
                <Text
                  fontSize="9px"
                  fontWeight="700"
                  color="#94A3B8"
                  textTransform="uppercase"
                  letterSpacing="0.1em"
                  bg="rgba(148, 163, 184, 0.14)"
                  px={2}
                  py={0.5}
                  borderRadius="full"
                >
                  Em breve
                </Text>
              )}
              <InfoTip label={atalho.info} size={14} />
            </Flex>
          </Flex>
          <Box>
            <Text fontSize="sm" fontWeight="600" color="#0F172A" mb={0.5}>
              {atalho.label}
            </Text>
            <Text fontSize="11px" color="#64748B" lineHeight="1.4">
              {atalho.description}
            </Text>
          </Box>
        </Flex>
      </GlassCard>
    </motion.div>
  );
};

interface AtalhosNavegacionaisProps {
  onImportarClick?: () => void;
}

export const AtalhosNavegacionais = ({ onImportarClick }: AtalhosNavegacionaisProps) => {
  const { isTechnical } = useAuth();
  const visiveis = ATALHOS.filter(a => !a.tecnico || isTechnical());
  return (
    <Box mb={6}>
      <Heading
        as="h2"
        fontSize="sm"
        fontWeight="600"
        color="#0F172A"
        mb={4}
        letterSpacing="-0.01em"
      >
        Atalhos do módulo
      </Heading>

      {/* Destaque: o cockpit */}
      <Box mb={5}>
        <AtalhoCard atalho={DASHBOARD_ATALHO} index={0} />
      </Box>

      <Flex direction="column" gap={5}>
        {GRUPOS.map(grupo => {
          const itens = visiveis.filter(a => a.grupo === grupo.key);
          if (itens.length === 0) return null;
          return (
            <Box key={grupo.key}>
              <Flex align="center" gap={2} mb={3}>
                <Box w="7px" h="7px" borderRadius="full" bg={grupo.dot} flexShrink={0} />
                <Text
                  fontSize="10px"
                  fontWeight="700"
                  color="#94A3B8"
                  textTransform="uppercase"
                  letterSpacing="0.1em"
                  whiteSpace="nowrap"
                >
                  {grupo.titulo}
                </Text>
                <Box flex={1} h="1px" bg="rgba(15, 23, 42, 0.07)" />
              </Flex>
              <SimpleGrid columns={{ base: 2, md: 3, lg: 3 }} spacing={4}>
                {itens.map((atalho, idx) => (
                  <AtalhoCard
                    key={atalho.path}
                    atalho={atalho}
                    index={idx}
                    onImportarClick={onImportarClick}
                  />
                ))}
              </SimpleGrid>
            </Box>
          );
        })}
      </Flex>
    </Box>
  );
};
